import { useToggle } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import qs from "qs";
import type { FunctionComponent } from "react";
import React, { useEffect, useMemo, useState } from "react";

import { useLoginDialogStateHelper } from "~/components/login-dialog/hooks";
import { getGoogleAnalyticsClientId } from "~/components/tracking/google-analytics/apis";
import { accountAtom } from "~/lib/account";
import { useParsedCookie } from "~/lib/hooks";
import { rosettaAtom } from "~/lib/rosetta";
import { useRouterReplaceWithSanitizedKeys } from "~/lib/router/hooks";

import { determineUserType, generateRecommendedPromises, sanitizePromisesToMap } from "./apis";
import { SanitizedUrlKeys } from "./consts";
import { DynamicOnboardingModal } from "./dynamics";
import { useNeedToShowOnboarding } from "./hooks";
import type {
  OnboardingSlide,
  OnboardQueryStringPayload,
  RecommendedContentEntityIds,
  UserType,
} from "./types";

export const Onboarding: FunctionComponent = () => {
  const [entityIdMap, setEntityIdMap] = useState<
    Record<OnboardingSlide, RecommendedContentEntityIds> | null | Record<string, never>
  >();
  const [isShowOnboardingModal, setIsShowOnboardingModal] = useToggle(false);
  const [onboardingSlides, setOnboardingSlides] = useState<OnboardingSlide[]>([]);
  const [userType, setUserType] = useState<UserType | null>(null);

  const { openLoginDialog } = useLoginDialogStateHelper();
  const { user } = useAtomValue(accountAtom);
  const { pathname, query } = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();

  const { value: forcedQaUserType } = useParsedCookie("qa_user_type", raw => raw, {
    initializeWithValue: true,
  });

  const asyncRosettaState = useAtomValue(rosettaAtom);

  const userTypeToOnboardingFlow: Record<UserType, OnboardingSlide[]> = useMemo(
    () => ({
      "008": ["newsletterSubscription", "conversations", "rosettaSubscription"],
      "014": ["myNews", "recommendedArticles", "rosettaSubscription"],
      "016": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "017": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "028": ["newsletterSubscription", "myNews", "recommendedArticles"],
      "029": ["newsletterSubscription", "myNews", "recommendedArticles"],
      "030": ["myNews", "evergreenArticles", "rosettaSubscription"],
      "038": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "057": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "058": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "083": ["myNews", "newsletterSubscription", "recommendedArticles"],
      "084": ["myNews", "newsletterSubscription", "recommendedArticles"],
      amp: ["myNews", "newsletterSubscription", "appDownload"],
      default: ["newsletterSubscription", "myNews", "rosettaSubscription"],
      myNews: ["newsletterSubscription", "appDownload", "rosettaSubscription"],
      newsletter: ["myNews", "appDownload", "rosettaSubscription"],
      subscribers: ["myNews", "newsletterSubscription", "appDownload"],
    }),
    [],
  );

  const needToShow = useNeedToShowOnboarding();

  useEffect(() => {
    const handleSanitizeRosettaSlide = (slides: OnboardingSlide[]) => {
      if (asyncRosettaState?.result?.contentServiceUserInfo?.isShowRosetta) return slides;
      return slides.filter(slide => slide !== "rosettaSubscription");
    };
    const run = async () => {
      const gaClientId = await getGoogleAnalyticsClientId();
      if (!needToShow || asyncRosettaState?.result?.isScmpSubscriber === undefined) return;

      // NOTE: Showing the modal can also be triggered at popup-container/sign-up-action-redirect,
      //       that is why state is handled by global state and not useState
      const { onboard, ...parsedQueryString } = qs.parse(window.location.search, {
        ignoreQueryPrefix: true,
      }) as unknown as OnboardQueryStringPayload;
      if (onboard && onboard !== "true") {
        void replace(
          SanitizedUrlKeys,
          { pathname, query },
          {
            shallow: true,
          },
        );

        return;
      } else if (onboard === "true" && !user?.uuid) {
        openLoginDialog({ destination: window.location.href });
      } else if (onboard === "true" && user?.uuid && gaClientId) {
        const userType = await determineUserType({
          forcedQaUserType: forcedQaUserType ?? "",
          gaClientId,
          isAmp: user?.source.includes("amp"),
          isFinishedMyNews: parsedQueryString.finishedMyNews === "true",
          isScmpSubscriber: asyncRosettaState?.result?.isScmpSubscriber ?? false,
        });

        setUserType(userType);
        setOnboardingSlides(handleSanitizeRosettaSlide(userTypeToOnboardingFlow[userType]));
        setIsShowOnboardingModal(true);
      }
    };

    void run();
  }, [
    forcedQaUserType,
    openLoginDialog,
    pathname,
    query,
    replace,
    setIsShowOnboardingModal,
    user?.source,
    user?.uuid,
    userTypeToOnboardingFlow,
    needToShow,
    asyncRosettaState?.result?.contentServiceUserInfo?.isShowRosetta,
    asyncRosettaState?.result?.isScmpSubscriber,
  ]);

  useEffect(() => {
    if (!isShowOnboardingModal) return;

    const run = async () => {
      const requiredPromises = generateRecommendedPromises(onboardingSlides, user?.uuid ?? "");
      if (requiredPromises.length === 0) {
        setEntityIdMap({});
      } else {
        const resolvedPromises = await Promise.all(
          requiredPromises.map(async promise => await promise()()),
        );
        setEntityIdMap(sanitizePromisesToMap(resolvedPromises));
      }
    };
    void run();
  }, [onboardingSlides, user?.uuid, isShowOnboardingModal]);

  return isShowOnboardingModal && userType && entityIdMap && asyncRosettaState ? (
    <DynamicOnboardingModal
      entityIdMap={entityIdMap}
      onboardingSlides={onboardingSlides}
      userType={userType}
      onLastSlide={() => {
        setIsShowOnboardingModal(false);
        // Move the replace to here is because we still need the query string to toggle the visibility of this component
        void replace(
          SanitizedUrlKeys,
          { pathname, query },
          {
            shallow: true,
          },
        );
      }}
    />
  ) : null;
};

Onboarding.displayName = "Onboarding";
